import { differenceInYears, parse } from 'date-fns';
import { dateToUTCMidnight, toUTCMidnight } from '@/lib/utils/common/dateFormats';

export const getMonthName = (date: Date) => {
  return date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' }).toUpperCase();
};

export const formatDateHeader = (date: Date) => {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${month}/${day}`;
};

export const getQuarter = (date: Date): number => {
  return Math.floor(date.getUTCMonth() / 3) + 1;
};

export const getQuarterLabel = (quarter: number, year: number): string => {
  return `Q${quarter} ${year}`;
};

const MAX_YEARS_FOR_MONTH_VIEW = 1;

export const shouldUseQuarterView = (from: string, to: string): boolean => {
  const fromDate = new Date(from);
  const toDate = new Date(to);
  const diffInYears = differenceInYears(toDate, fromDate);
  return diffInYears >= MAX_YEARS_FOR_MONTH_VIEW;
};

export const getTwoWeekRangesInMonth = ({
  year,
  month,
  from,
  to,
}: {
  year: number;
  month: number;
  from: string;
  to: string;
}) => {
  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const toDate = dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date()));

  const monthStart = toUTCMidnight(year, month, 1);
  const monthEnd = toUTCMidnight(year, month + 1, 0);

  const ranges: { start: Date; end: Date }[] = [];

  let currentStart = fromDate;

  while (currentStart <= toDate) {
    const currentEnd = toUTCMidnight(
      currentStart.getUTCFullYear(),
      currentStart.getUTCMonth(),
      currentStart.getUTCDate() + 13,
    );

    const rangeEnd = currentEnd > toDate ? toDate : currentEnd;

    if (currentStart >= monthStart && currentStart <= monthEnd) {
      ranges.push({
        start: currentStart,
        end: rangeEnd,
      });
    }

    currentStart = toUTCMidnight(
      currentStart.getUTCFullYear(),
      currentStart.getUTCMonth(),
      currentStart.getUTCDate() + 14,
    );
  }

  if (ranges.length === 0 && monthEnd < fromDate) {
    let backtrackStart = toUTCMidnight(
      fromDate.getUTCFullYear(),
      fromDate.getUTCMonth(),
      fromDate.getUTCDate() - 14,
    );

    while (backtrackStart >= monthStart) {
      const backtrackEnd = toUTCMidnight(
        backtrackStart.getUTCFullYear(),
        backtrackStart.getUTCMonth(),
        backtrackStart.getUTCDate() + 13,
      );

      const rangeEnd = toUTCMidnight(
        fromDate.getUTCFullYear(),
        fromDate.getUTCMonth(),
        fromDate.getUTCDate() - 1,
      );

      if (backtrackEnd >= monthStart && backtrackEnd <= monthEnd) {
        ranges.unshift({
          start: backtrackStart < monthStart ? monthStart : backtrackStart,
          end: backtrackEnd < rangeEnd ? backtrackEnd : rangeEnd,
        });
      } else if (backtrackEnd > monthEnd) {
        ranges.unshift({
          start: backtrackStart < monthStart ? monthStart : backtrackStart,
          end: rangeEnd < monthEnd ? rangeEnd : monthEnd,
        });
        break;
      }

      backtrackStart = toUTCMidnight(
        backtrackStart.getUTCFullYear(),
        backtrackStart.getUTCMonth(),
        backtrackStart.getUTCDate() - 14,
      );
    }
  }

  return ranges;
};

export const getTimelineColumnRangesInQuarter = ({
  year,
  quarter,
  from,
  to,
}: {
  year: number;
  quarter: number;
  from: string;
  to: string;
}) => {
  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;

  const fromDate = dateToUTCMidnight(parse(from, 'yyyy-MM-dd', new Date()));
  const toDate = dateToUTCMidnight(parse(to, 'yyyy-MM-dd', new Date()));

  const months: { start: Date; end: Date }[] = [];

  for (let monthIndex = startMonth; monthIndex <= endMonth; monthIndex++) {
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0);
    const utcStart = dateToUTCMidnight(monthStart);
    const utcEnd = dateToUTCMidnight(monthEnd);
    if (utcEnd < fromDate || utcStart > toDate) {
      continue;
    }

    let rangeStart = utcStart;
    let rangeEnd = utcEnd;

    if (rangeStart < fromDate) {
      rangeStart = fromDate;
    }

    if (rangeEnd > toDate) {
      rangeEnd = toDate;
    }

    months.push({
      start: rangeStart,
      end: rangeEnd,
    });
  }

  return months;
};
